import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../utils/axiosConfig";
import toast from "react-hot-toast";
import "../styles/ApplicationKanban.css";

interface JobApplication {
  id: string;
  jobTitle: string;
  company: string;
  location: string;
  status: "applied" | "interview_scheduled" | "rejected" | "offered" | "accepted";
  appliedAt: string;
  interviewDate: string | null;
  notes: string;
  url: string;
  // New fields for enhanced tracking
  source?: "manual" | "email" | "crawl";
  extractedSkills?: string[];
  extractedTools?: string[];
  experienceRequired?: string;
  certificationsRequired?: string[];
  companyIndustry?: string;
  companyMission?: string;
  companyValues?: string;
  companyVision?: string;
  metadataExtracted?: boolean;
  positionEndDate?: string;
  resumeUsedId?: string | null;
  resumeUsed?: { id: string; fileName: string } | null;
  emailCount?: number;
  lastEmail?: {
    subject: string;
    receivedAt: string;
    direction: "inbound" | "outbound";
    detectedStatus?: string | null;
  } | null;
  applicationAddress?: string | null;
  tags?: string[];
  interviewRating?: number | null;
  interviewNotes?: string | null;
}

interface EmailAccount {
  id: string;
  provider: string;
  email: string;
  isConnected: boolean;
  lastSyncAt: string | null;
}

interface Stats {
  totalApplications: number;
  totalSavedJobs: number;
  applicationsByStatus: {
    applied: number;
    interview_scheduled: number;
    rejected: number;
    offered: number;
    accepted: number;
  };
  successRate: string;
}

type ViewMode = "kanban" | "list" | "timeline";

const COLUMNS = [
  { key: "applied", label: "Applied", icon: "📝", color: "#6366f1" },
  { key: "interview_scheduled", label: "Interview", icon: "📅", color: "#f59e0b" },
  { key: "offered", label: "Offers", icon: "🎁", color: "#10b981" },
  { key: "accepted", label: "Accepted", icon: "✅", color: "#22c55e" },
  { key: "rejected", label: "Unsuccessful Attempt", icon: "❌", color: "#ef4444" },
];

const ApplicationKanban: React.FC = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("kanban");
  const [draggedCard, setDraggedCard] = useState<string | null>(null);
  const [selectedApp, setSelectedApp] = useState<JobApplication | null>(null);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [feedbackApp, setFeedbackApp] = useState<JobApplication | null>(null);
  const [feedbackRating, setFeedbackRating] = useState(0);
  const [feedbackNotes, setFeedbackNotes] = useState("");
  const [savingFeedback, setSavingFeedback] = useState(false);

  // Quick add form state
  const [quickAddData, setQuickAddData] = useState({
    jobTitle: "",
    company: "",
    location: "",
    url: "",
    notes: "",
    jobDescription: "",
    extractMetadata: true,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [appsResponse, statsResponse] = await Promise.all([
        axiosInstance.get("/auth/jobs/applications"),
        axiosInstance.get("/auth/jobs/stats"),
      ]);
      setApplications(appsResponse.data.applications);
      setStats(statsResponse.data);
    } catch (err: any) {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch email accounts to check sync status
  const fetchEmailAccounts = useCallback(async () => {
    try {
      const response = await axiosInstance.get("/auth/email/accounts");
      setEmailAccounts(response.data.emailAccounts || []);
    } catch (err) {
      // User may not have email integration set up
      console.log("Email accounts not available");
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchEmailAccounts();
  }, [fetchData, fetchEmailAccounts]);

  // Sync emails and update applications
  const handleEmailSync = async () => {
    const connectedAccounts = emailAccounts.filter(acc => acc.isConnected);
    if (connectedAccounts.length === 0) {
      toast.error("No connected email accounts. Go to Settings to connect your email.");
      return;
    }
    
    setSyncing(true);
    let totalSynced = 0;
    let totalUpdates = 0;
    let aiAssistedUpdates = 0;
    let outboundCreated = 0;
    let outboundLinked = 0;
    let inboundCreated = 0;
    let statusUpdated = 0;
    let resumeLinked = 0;

    try {
      for (const account of connectedAccounts) {
        const response = await axiosInstance.post("/auth/email/sync", {
          emailAccountId: account.id,
        });
        totalSynced += response.data.syncedCount || 0;
        const upd: any[] = response.data.updates || [];
        totalUpdates += upd.length;
        for (const u of upd) {
          if (u?.classificationSource === "llm" || u?.classificationSource === "llm+heuristic") {
            aiAssistedUpdates++;
          }
          if (u?.action === "auto_created" && u?.resumeLinked !== undefined) outboundCreated++;
          else if (u?.action === "auto_created") inboundCreated++;
          if (u?.action === "linked_existing") outboundLinked++;
          if (u?.action === "status_updated") statusUpdated++;
          if (u?.resumeLinked) resumeLinked++;
        }
      }

      if (totalUpdates > 0) {
        const parts: string[] = [];
        if (outboundCreated) parts.push(`${outboundCreated} new from sent`);
        if (inboundCreated) parts.push(`${inboundCreated} new from inbox`);
        if (statusUpdated) parts.push(`${statusUpdated} status updated`);
        if (outboundLinked) parts.push(`${outboundLinked} linked`);
        if (resumeLinked) parts.push(`${resumeLinked} resume\u00A0linked`);
        if (aiAssistedUpdates) parts.push(`${aiAssistedUpdates} AI\u00A0classified`);
        toast.success(`Synced ${totalSynced} emails — ${parts.join(", ")}`);
        await fetchData();
      } else if (totalSynced > 0) {
        toast.success(`Synced ${totalSynced} new emails`);
        await fetchData();
      } else {
        toast("No new emails found", { icon: "📭" });
      }
    } catch (err: any) {
      if (err.response?.data?.consentRequired) {
        toast.error("Email access not enabled. Please grant permission in Settings.");
      } else {
        toast.error("Failed to sync emails");
      }
    } finally {
      setSyncing(false);
    }
  };


  // Interview reminder notifications
  useEffect(() => {
    if (applications.length === 0) return;

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const dayAfter = new Date(tomorrow);
    dayAfter.setDate(dayAfter.getDate() + 1);

    const todayInterviews = applications.filter(app => {
      if (app.status !== "interview_scheduled" || !app.interviewDate) return false;
      const interviewDate = new Date(app.interviewDate);
      return interviewDate >= today && interviewDate < tomorrow;
    });

    const tomorrowInterviews = applications.filter(app => {
      if (app.status !== "interview_scheduled" || !app.interviewDate) return false;
      const interviewDate = new Date(app.interviewDate);
      return interviewDate >= tomorrow && interviewDate < dayAfter;
    });

    // Show reminder toasts
    if (todayInterviews.length > 0) {
      setTimeout(() => {
        toast(`🚨 You have ${todayInterviews.length} interview${todayInterviews.length > 1 ? 's' : ''} TODAY!`, {
          duration: 6000,
          icon: '📅',
        });
        todayInterviews.forEach(app => {
          toast(`${app.company} - ${app.jobTitle}`, { duration: 5000 });
        });
      }, 1500);
    }

    if (tomorrowInterviews.length > 0) {
      setTimeout(() => {
        toast(`📆 Reminder: ${tomorrowInterviews.length} interview${tomorrowInterviews.length > 1 ? 's' : ''} tomorrow`, {
          duration: 5000,
          icon: '🔔',
        });
      }, 3000);
    }

    // Request browser notification permission and show notifications
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
    
    if ("Notification" in window && Notification.permission === "granted" && todayInterviews.length > 0) {
      todayInterviews.forEach(app => {
        new Notification(`Interview Today: ${app.company}`, {
          body: `${app.jobTitle} interview scheduled for today`,
          icon: '📅',
        });
      });
    }
  }, [applications]);

  const handleDragStart = (e: React.DragEvent, appId: string) => {
    setDraggedCard(appId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    if (!draggedCard) return;

    const app = applications.find(a => a.id === draggedCard);
    if (!app || app.status === newStatus) {
      setDraggedCard(null);
      return;
    }

    // Optimistically update UI
    setApplications(prev => 
      prev.map(a => a.id === draggedCard ? { ...a, status: newStatus as any } : a)
    );

    try {
      await axiosInstance.put(`/auth/jobs/applications/${draggedCard}`, { status: newStatus });
      toast.success(`Moved to ${COLUMNS.find(c => c.key === newStatus)?.label}`);
      
      // Refresh stats
      const statsResponse = await axiosInstance.get("/auth/jobs/stats");
      setStats(statsResponse.data);
    } catch (err) {
      // Revert on error
      fetchData();
      toast.error("Failed to update status");
    }

    setDraggedCard(null);
  };

  const handleQuickAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!quickAddData.jobTitle.trim() || !quickAddData.company.trim()) {
      toast.error("Job title and company are required");
      return;
    }
    if (quickAddData.jobTitle.length > 300 || quickAddData.company.length > 300) {
      toast.error("Job title or company is too long (max 300 characters)");
      return;
    }
    if (quickAddData.url && !/^https?:\/\/.+/i.test(quickAddData.url.trim())) {
      toast.error("URL must start with http:// or https://");
      return;
    }

    try {
      await axiosInstance.post("/auth/jobs/applications/manual", {
        jobTitle: quickAddData.jobTitle,
        company: quickAddData.company,
        location: quickAddData.location || "Not specified",
        url: quickAddData.url || "",
        notes: quickAddData.notes || "",
        jobDescription: quickAddData.jobDescription || "",
        extractMetadata: quickAddData.extractMetadata,
      });
      
      toast.success("Application added!");
      setShowQuickAdd(false);
      setQuickAddData({ 
        jobTitle: "", company: "", location: "", url: "", notes: "",
        jobDescription: "", extractMetadata: true
      });
      fetchData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || "Failed to add application");
    }
  };

  const handleSaveFeedback = async () => {
    if (!feedbackApp) return;
    setSavingFeedback(true);
    try {
      await axiosInstance.patch(`/auth/jobs/applications/${feedbackApp.id}/feedback`, {
        interviewRating: feedbackRating || undefined,
        interviewNotes: feedbackNotes || undefined,
      });
      setApplications(prev => prev.map(a =>
        a.id === feedbackApp.id
          ? { ...a, interviewRating: feedbackRating || null, interviewNotes: feedbackNotes || null }
          : a
      ));
      toast.success("Feedback saved!");
      setFeedbackApp(null);
      setFeedbackRating(0);
      setFeedbackNotes("");
    } catch {
      toast.error("Failed to save feedback");
    } finally {
      setSavingFeedback(false);
    }
  };

  const handleDeleteApplication = async (appId: string) => {
    if (!window.confirm("Remove this application?")) return;

    try {
      await axiosInstance.delete(`/auth/jobs/applications/${appId}`);
      setApplications(prev => prev.filter(a => a.id !== appId));
      setSelectedApp(null);
      toast.success("Application removed");
      
      const statsResponse = await axiosInstance.get("/auth/jobs/stats");
      setStats(statsResponse.data);
    } catch (err) {
      toast.error("Failed to remove application");
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  };

  const getDaysAgo = (dateStr: string) => {
    const days = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return "Today";
    if (days === 1) return "Yesterday";
    return `${days} days ago`;
  };

  const getUpcomingInterviews = () => {
    const now = new Date();
    return applications
      .filter(app => 
        app.status === "interview_scheduled" && 
        app.interviewDate && 
        new Date(app.interviewDate) >= now
      )
      .sort((a, b) => new Date(a.interviewDate!).getTime() - new Date(b.interviewDate!).getTime())
      .slice(0, 5);
  };

  const renderKanbanView = () => (
    <div className="ak-kanban-container">
      {COLUMNS.map(column => {
        const columnApps = applications.filter(app => app.status === column.key);
        
        return (
          <div
            key={column.key}
            className={`ak-column ${draggedCard ? "ak-column-droppable" : ""}`}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, column.key)}
          >
            <div className="ak-column-header" style={{ borderColor: column.color }}>
              <span className="ak-column-icon">{column.icon}</span>
              <h3>{column.label}</h3>
              <span className="ak-column-count">{columnApps.length}</span>
            </div>

            <div className="ak-column-cards">
              {columnApps.map(app => (
                <div
                  key={app.id}
                  className={`ak-card ${draggedCard === app.id ? "ak-card-dragging" : ""}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, app.id)}
                  onClick={() => setSelectedApp(app)}
                >
                  <div className="ak-card-header">
                    <h4 className="ak-card-title">{app.jobTitle}</h4>
                    {app.source === "email" && (
                      <span className="ak-source-badge ak-source-email" title="Auto-detected from email">📧</span>
                    )}
                    {app.metadataExtracted && (
                      <span className="ak-source-badge ak-source-ai" title="AI-extracted metadata">🤖</span>
                    )}
                    {app.resumeUsed && (
                      <span
                        className="ak-source-badge"
                        title={`Resume used: ${app.resumeUsed.fileName}`}
                        style={{ background: "#e5e7eb", color: "#1f2937" }}
                      >
                        📎
                      </span>
                    )}
                    {(app.emailCount ?? 0) > 0 && (
                      <span
                        className="ak-source-badge"
                        title={`${app.emailCount} email${app.emailCount === 1 ? "" : "s"} on this thread`}
                        style={{ background: "#e5e7eb", color: "#1f2937" }}
                      >
                        ✉ {app.emailCount}
                      </span>
                    )}
                  </div>
                  <p className="ak-card-company">{app.company}</p>
                  <div className="ak-card-meta">
                    <span className="ak-card-location">📍 {app.location}</span>
                    <span className="ak-card-date">{getDaysAgo(app.appliedAt)}</span>
                  </div>
                  {app.interviewDate && (
                    <div className="ak-card-interview">
                      📅 {formatDate(app.interviewDate)}
                    </div>
                  )}
                  {app.extractedSkills && app.extractedSkills.length > 0 && (
                    <div className="ak-card-skills">
                      {app.extractedSkills.slice(0, 3).map((skill, i) => (
                        <span key={i} className="ak-skill-tag">{skill}</span>
                      ))}
                      {app.extractedSkills.length > 3 && (
                        <span className="ak-skill-more">+{app.extractedSkills.length - 3}</span>
                      )}
                    </div>
                  )}
                  {app.tags && app.tags.length > 0 && (
                    <div className="ak-card-tags">
                      {app.tags.slice(0, 3).map((tag, i) => {
                        const [ns, val] = tag.includes(":") ? tag.split(":") : ["", tag];
                        return (
                          <span key={i} className={`ak-auto-tag ak-tag-${ns || "other"}`}>
                            {val || tag}
                          </span>
                        );
                      })}
                      {app.tags.length > 3 && (
                        <span className="ak-skill-more">+{app.tags.length - 3}</span>
                      )}
                    </div>
                  )}
                  {app.notes && !app.extractedSkills?.length && (
                    <div className="ak-card-notes">
                      💬 {app.notes.substring(0, 50)}...
                    </div>
                  )}
                </div>
              ))}

              {columnApps.length === 0 && (
                <div className="ak-column-empty">
                  Drop applications here
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );

  const renderTimelineView = () => {
    const sortedApps = [...applications].sort(
      (a, b) => new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime()
    );

    return (
      <div className="ak-timeline-container">
        {sortedApps.map((app, index) => {
          const column = COLUMNS.find(c => c.key === app.status);
          return (
            <div key={app.id} className="ak-timeline-item">
              <div className="ak-timeline-marker" style={{ backgroundColor: column?.color }} />
              <div className="ak-timeline-connector" />
              <div className="ak-timeline-content" onClick={() => setSelectedApp(app)}>
                <div className="ak-timeline-date">{formatDate(app.appliedAt)}</div>
                <div className="ak-timeline-card">
                  <div className="ak-timeline-header">
                    <h4>{app.jobTitle}</h4>
                    <span className="ak-timeline-status" style={{ backgroundColor: column?.color }}>
                      {column?.icon} {column?.label}
                    </span>
                  </div>
                  <p className="ak-timeline-company">{app.company} • {app.location}</p>
                  {app.notes && <p className="ak-timeline-notes">{app.notes}</p>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderListView = () => (
    <div className="ak-list-container">
      <table className="ak-table">
        <thead>
          <tr>
            <th>Position</th>
            <th>Company</th>
            <th>Location</th>
            <th>Status</th>
            <th>Applied</th>
            <th>Interview</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {applications.map(app => {
            const column = COLUMNS.find(c => c.key === app.status);
            return (
              <tr key={app.id} onClick={() => setSelectedApp(app)}>
                <td className="ak-table-title">{app.jobTitle}</td>
                <td>{app.company}</td>
                <td>{app.location}</td>
                <td>
                  <span className="ak-table-status" style={{ backgroundColor: column?.color }}>
                    {column?.icon} {column?.label}
                  </span>
                </td>
                <td>{formatDate(app.appliedAt)}</td>
                <td>{app.interviewDate ? formatDate(app.interviewDate) : "-"}</td>
                <td>
                  <button 
                    className="ak-action-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (app.url) window.open(app.url, "_blank");
                    }}
                    disabled={!app.url}
                  >
                    🔗
                  </button>
                  <button 
                    className="ak-action-btn ak-action-delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteApplication(app.id);
                    }}
                  >
                    🗑️
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderDetailModal = () => {
    if (!selectedApp) return null;
    const column = COLUMNS.find(c => c.key === selectedApp.status);

    return (
      <div className="ak-modal-overlay" onClick={() => setSelectedApp(null)}>
        <div className="ak-modal" onClick={(e) => e.stopPropagation()}>
          <button className="ak-modal-close" onClick={() => setSelectedApp(null)}>×</button>
          
          <div className="ak-modal-header" style={{ borderColor: column?.color }}>
            <h2>{selectedApp.jobTitle}</h2>
            <span className="ak-modal-status" style={{ backgroundColor: column?.color }}>
              {column?.icon} {column?.label}
            </span>
          </div>

          <div className="ak-modal-body">
            {/* Source indicator */}
            {selectedApp.source && selectedApp.source !== 'manual' && (
              <div className="ak-modal-source">
                {selectedApp.source === 'email' && <span className="ak-source-badge ak-source-email">📧 From Email</span>}
                {selectedApp.metadataExtracted && <span className="ak-source-badge ak-source-ai">🤖 AI-Enhanced</span>}
              </div>
            )}

            {/* Email + resume linkage panel */}
            {(selectedApp.resumeUsed || selectedApp.lastEmail || selectedApp.applicationAddress) && (
              <div
                style={{
                  margin: "8px 0 16px",
                  padding: "12px 14px",
                  background: "#f9fafb",
                  border: "1px solid #e5e7eb",
                  borderLeft: "3px solid #2563eb",
                  borderRadius: 6,
                  fontSize: 13,
                  color: "#1f2937",
                }}
              >
                {selectedApp.resumeUsed && (
                  <div style={{ marginBottom: 6 }}>
                    <span style={{ color: "#6b7280" }}>📎 Resume used:</span>{" "}
                    <strong>{selectedApp.resumeUsed.fileName}</strong>
                  </div>
                )}
                {selectedApp.applicationAddress && (
                  <div style={{ marginBottom: 6 }}>
                    <span style={{ color: "#6b7280" }}>📨 Applied via:</span>{" "}
                    <span>{selectedApp.applicationAddress}</span>
                  </div>
                )}
                {selectedApp.lastEmail && (
                  <div>
                    <span style={{ color: "#6b7280" }}>
                      ✉ Last email ({selectedApp.lastEmail.direction === "outbound" ? "you sent" : "received"}
                      {selectedApp.lastEmail.detectedStatus ? ` · ${selectedApp.lastEmail.detectedStatus}` : ""}):
                    </span>{" "}
                    <span title={selectedApp.lastEmail.subject}>
                      {selectedApp.lastEmail.subject.slice(0, 80)}
                      {selectedApp.lastEmail.subject.length > 80 ? "…" : ""}
                    </span>{" "}
                    <span style={{ color: "#6b7280" }}>
                      · {getDaysAgo(selectedApp.lastEmail.receivedAt)}
                    </span>
                  </div>
                )}
                {(selectedApp.emailCount ?? 0) > 1 && (
                  <div style={{ marginTop: 4, color: "#6b7280" }}>
                    {selectedApp.emailCount} emails on this thread
                  </div>
                )}
              </div>
            )}

            <div className="ak-modal-row">
              <label>Company</label>
              <p>{selectedApp.company}</p>
            </div>
            <div className="ak-modal-row">
              <label>Location</label>
              <p>{selectedApp.location}</p>
            </div>
            <div className="ak-modal-row">
              <label>Applied</label>
              <p>{new Date(selectedApp.appliedAt).toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric"
              })}</p>
            </div>
            {selectedApp.interviewDate && (
              <div className="ak-modal-row">
                <label>Interview Date</label>
                <p>{new Date(selectedApp.interviewDate).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric"
                })}</p>
              </div>
            )}

            {/* Extracted Skills */}
            {selectedApp.extractedSkills && selectedApp.extractedSkills.length > 0 && (
              <div className="ak-modal-row">
                <label>Required Skills</label>
                <div className="ak-modal-tags">
                  {selectedApp.extractedSkills.map((skill, idx) => (
                    <span key={idx} className="ak-tag ak-tag-skill">{skill}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Extracted Tools */}
            {selectedApp.extractedTools && selectedApp.extractedTools.length > 0 && (
              <div className="ak-modal-row">
                <label>Tools & Technologies</label>
                <div className="ak-modal-tags">
                  {selectedApp.extractedTools.map((tool, idx) => (
                    <span key={idx} className="ak-tag ak-tag-tool">{tool}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Experience Required */}
            {selectedApp.experienceRequired && (
              <div className="ak-modal-row">
                <label>Experience Required</label>
                <p className="ak-experience-badge">{selectedApp.experienceRequired}</p>
              </div>
            )}

            {/* Certifications */}
            {selectedApp.certificationsRequired && selectedApp.certificationsRequired.length > 0 && (
              <div className="ak-modal-row">
                <label>Certifications</label>
                <div className="ak-modal-tags">
                  {selectedApp.certificationsRequired.map((cert, idx) => (
                    <span key={idx} className="ak-tag ak-tag-cert">{cert}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Company Info Section */}
            {(selectedApp.companyIndustry || selectedApp.companyMission || selectedApp.companyValues) && (
              <div className="ak-modal-section">
                <h3>🏢 Company Information</h3>
                {selectedApp.companyIndustry && (
                  <div className="ak-modal-row">
                    <label>Industry</label>
                    <p>{selectedApp.companyIndustry}</p>
                  </div>
                )}
                {selectedApp.companyMission && (
                  <div className="ak-modal-row">
                    <label>Mission</label>
                    <p className="ak-company-text">{selectedApp.companyMission}</p>
                  </div>
                )}
                {selectedApp.companyValues && (
                  <div className="ak-modal-row">
                    <label>Values</label>
                    <p className="ak-company-text">{selectedApp.companyValues}</p>
                  </div>
                )}
                {selectedApp.companyVision && (
                  <div className="ak-modal-row">
                    <label>Vision</label>
                    <p className="ak-company-text">{selectedApp.companyVision}</p>
                  </div>
                )}
              </div>
            )}

            {selectedApp.notes && (
              <div className="ak-modal-row">
                <label>Notes</label>
                <p>{selectedApp.notes}</p>
              </div>
            )}
          </div>

          <div className="ak-modal-actions">
            {selectedApp.url && (
              <button 
                className="ak-btn ak-btn-secondary"
                onClick={() => window.open(selectedApp.url, "_blank")}
              >
                🔗 View Job Posting
              </button>
            )}
            <button
              className="ak-btn ak-btn-secondary"
              onClick={() => {
                setFeedbackApp(selectedApp);
                setFeedbackRating(selectedApp.interviewRating || 0);
                setFeedbackNotes(selectedApp.interviewNotes || "");
              }}
            >
              ⭐ Interview Feedback
            </button>
            <button
              className="ak-btn ak-btn-danger"
              onClick={() => handleDeleteApplication(selectedApp.id)}
            >
              🗑️ Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderFeedbackModal = () => {
    if (!feedbackApp) return null;
    return (
      <div className="ak-modal-overlay" onClick={() => setFeedbackApp(null)}>
        <div className="ak-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
          <button className="ak-modal-close" onClick={() => setFeedbackApp(null)}>×</button>
          <h2>Interview Feedback</h2>
          <p className="ak-modal-subtitle">{feedbackApp.jobTitle} at {feedbackApp.company}</p>

          <div style={{ margin: "16px 0" }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>Rating</label>
            <div style={{ display: "flex", gap: 8 }}>
              {[1,2,3,4,5].map(n => (
                <button
                  key={n}
                  onClick={() => setFeedbackRating(n)}
                  style={{
                    fontSize: "1.6rem", background: "none", border: "none", cursor: "pointer",
                    opacity: n <= feedbackRating ? 1 : 0.3, transition: "opacity 0.15s",
                  }}
                  title={`${n} star${n !== 1 ? "s" : ""}`}
                >
                  ⭐
                </button>
              ))}
            </div>
          </div>

          <div style={{ margin: "16px 0" }}>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>Notes</label>
            <textarea
              className="clb-tailor-textarea"
              rows={5}
              placeholder="How did the interview go? What would you do differently?"
              value={feedbackNotes}
              onChange={e => setFeedbackNotes(e.target.value)}
              style={{ width: "100%", boxSizing: "border-box" }}
            />
          </div>

          <div style={{ display: "flex", gap: 10 }}>
            <button
              className="ak-btn ak-btn-primary"
              onClick={handleSaveFeedback}
              disabled={savingFeedback}
            >
              {savingFeedback ? "Saving…" : "💾 Save Feedback"}
            </button>
            <button className="ak-btn ak-btn-secondary" onClick={() => setFeedbackApp(null)}>
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderQuickAddModal = () => {
    if (!showQuickAdd) return null;

    return (
      <div className="ak-modal-overlay" onClick={() => setShowQuickAdd(false)}>
        <div className="ak-modal" onClick={(e) => e.stopPropagation()}>
          <button className="ak-modal-close" onClick={() => setShowQuickAdd(false)}>×</button>
          
          <h2>Add Application</h2>
          <p className="ak-modal-subtitle">Track a job you've already applied to</p>

          <form onSubmit={handleQuickAdd} className="ak-quick-add-form">
            <div className="ak-form-row">
              <div className="ak-form-group">
                <label>Job Title *</label>
                <input
                  type="text"
                  value={quickAddData.jobTitle}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, jobTitle: e.target.value }))}
                  placeholder="e.g., Software Engineer"
                  required
                />
              </div>
              <div className="ak-form-group">
                <label>Company *</label>
                <input
                  type="text"
                  value={quickAddData.company}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, company: e.target.value }))}
                  placeholder="e.g., Google"
                  required
                />
              </div>
            </div>
            <div className="ak-form-row">
              <div className="ak-form-group">
                <label>Location</label>
                <input
                  type="text"
                  value={quickAddData.location}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, location: e.target.value }))}
                  placeholder="e.g., Remote"
                />
              </div>
              <div className="ak-form-group">
                <label>Job URL</label>
                <input
                  type="url"
                  value={quickAddData.url}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="ak-form-group">
              <label>Job Description (paste for AI extraction)</label>
              <textarea
                value={quickAddData.jobDescription}
                onChange={(e) => setQuickAddData(prev => ({ ...prev, jobDescription: e.target.value }))}
                placeholder="Paste the full job description here to auto-extract skills, requirements, and company info..."
                rows={5}
              />
            </div>
            <div className="ak-form-group ak-checkbox-group">
              <label>
                <input
                  type="checkbox"
                  checked={quickAddData.extractMetadata}
                  onChange={(e) => setQuickAddData(prev => ({ ...prev, extractMetadata: e.target.checked }))}
                />
                🤖 Auto-extract skills & requirements from job description
              </label>
            </div>
            <div className="ak-form-group">
              <label>Notes</label>
              <textarea
                value={quickAddData.notes}
                onChange={(e) => setQuickAddData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any notes about this application..."
                rows={2}
              />
            </div>
            <div className="ak-form-actions">
              <button type="button" className="ak-btn ak-btn-secondary" onClick={() => setShowQuickAdd(false)}>
                Cancel
              </button>
              <button type="submit" className="ak-btn ak-btn-primary">
                Add Application
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="ak-container">
        <div className="ak-loading">Loading your applications...</div>
      </div>
    );
  }

  const upcomingInterviews = getUpcomingInterviews();

  return (
    <div className="ak-container">
      {/* Header */}
      <div className="ak-header">
        <div className="ak-header-left">
          <button className="ak-back-btn" onClick={() => navigate("/dashboard")}>
            ← Dashboard
          </button>
          <h1>📋 Application Tracker</h1>
        </div>
        <div className="ak-header-right">
          <div className="ak-view-toggle">
            <button 
              className={`ak-view-btn ${viewMode === "kanban" ? "active" : ""}`}
              onClick={() => setViewMode("kanban")}
              title="Kanban Board"
            >
              ▦
            </button>
            <button 
              className={`ak-view-btn ${viewMode === "list" ? "active" : ""}`}
              onClick={() => setViewMode("list")}
              title="List View"
            >
              ☰
            </button>
            <button 
              className={`ak-view-btn ${viewMode === "timeline" ? "active" : ""}`}
              onClick={() => setViewMode("timeline")}
              title="Timeline"
            >
              ⋮
            </button>
          </div>
          <button 
            className={`ak-sync-btn ${syncing ? "syncing" : ""}`} 
            onClick={handleEmailSync}
            disabled={syncing}
            title={emailAccounts.length > 0 ? "Sync emails for application updates" : "Connect email in Settings"}
          >
            {syncing ? "🔄 Syncing..." : "📧 Sync Email"}
          </button>
          <button className="ak-add-btn" onClick={() => setShowQuickAdd(true)}>
            + Manual Add
          </button>
          <button className="ak-compare-btn" onClick={() => navigate("/offers/compare")}>
            ⚖️ Compare
          </button>
        </div>
      </div>

      {/* Stats Bar */}
      {stats && (
        <div className="ak-stats-bar">
          <div className="ak-stat">
            <span className="ak-stat-value">{stats.totalApplications}</span>
            <span className="ak-stat-label">Total</span>
          </div>
          <div className="ak-stat">
            <span className="ak-stat-value">{stats.applicationsByStatus.applied}</span>
            <span className="ak-stat-label">Applied</span>
          </div>
          <div className="ak-stat">
            <span className="ak-stat-value">{stats.applicationsByStatus.interview_scheduled}</span>
            <span className="ak-stat-label">Interviews</span>
          </div>
          <div className="ak-stat">
            <span className="ak-stat-value">{stats.applicationsByStatus.offered}</span>
            <span className="ak-stat-label">Offers</span>
          </div>
          <div className="ak-stat ak-stat-highlight">
            <span className="ak-stat-value">{stats.successRate}%</span>
            <span className="ak-stat-label">Success</span>
          </div>
        </div>
      )}

      {/* Upcoming Interviews Alert */}
      {upcomingInterviews.length > 0 && (
        <div className="ak-interviews-alert">
          <h3>📅 Upcoming Interviews</h3>
          <div className="ak-interviews-list">
            {upcomingInterviews.map(app => (
              <div key={app.id} className="ak-interview-item" onClick={() => setSelectedApp(app)}>
                <span className="ak-interview-date">
                  {new Date(app.interviewDate!).toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  })}
                </span>
                <span className="ak-interview-title">{app.jobTitle}</span>
                <span className="ak-interview-company">@ {app.company}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content */}
      {applications.length === 0 ? (
        <div className="ak-empty-state">
          <div className="ak-empty-icon">🎯</div>
          <h2>Start tracking your applications</h2>
          <p>Keep all your job applications organized in one place</p>
          <button className="ak-btn ak-btn-primary" onClick={() => setShowQuickAdd(true)}>
            + Add Your First Application
          </button>
        </div>
      ) : (
        <>
          {viewMode === "kanban" && renderKanbanView()}
          {viewMode === "list" && renderListView()}
          {viewMode === "timeline" && renderTimelineView()}
        </>
      )}

      {/* Modals */}
      {renderDetailModal()}
      {renderFeedbackModal()}
      {renderQuickAddModal()}
    </div>
  );
};

export default ApplicationKanban;
